import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * @param {HTMLElement} slide
   * @returns {HTMLAnchorElement | null}
   */
  #getVisibleSlideLink(slide) {
    const desktop = slide.querySelector('.slide__link--desktop');
    const mobile = slide.querySelector('.slide__link--mobile');

    if (mobile instanceof HTMLAnchorElement && getComputedStyle(mobile).display !== 'none') {
      return mobile;
    }

    return desktop instanceof HTMLAnchorElement ? desktop : null;
  }

  /**
   * @param {HTMLAnchorElement} slideLink
   */
  #navigateSlideLink(slideLink) {
    if (slideLink.target === '_blank') {
      window.open(slideLink.href, '_blank', 'noopener');
      return;
    }

    window.location.assign(slideLink.href);
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;

    const targetEl = event?.target instanceof Element ? event.target : null;
    if (targetEl && event) {
      if (targetEl.closest('.slide__link')) {
        return;
      }

      const slide =
        targetEl.tagName === 'SLIDESHOW-SLIDE'
          ? targetEl
          : targetEl.closest('slideshow-slide');
      const isInteractiveTarget = targetEl.closest(
        'button, a:not(.slide__link), input, textarea, select, [role="button"], .slide__video-controls, slideshow-controls, slideshow-arrows'
      );

      if (
        slide instanceof HTMLElement &&
        slide.getAttribute('aria-hidden') !== 'true' &&
        !isInteractiveTarget
      ) {
        const slideLink = this.#getVisibleSlideLink(slide);

        if (slideLink?.href && event.isTrusted) {
          this.#navigateSlideLink(slideLink);
          return;
        }
      }
    }

    if (!this.#scroll) return;

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (
      !instant &&
      !isAdjacentSlide &&
      visibleSlides.length === 1 &&
      !this.#shouldUseMobileLoopSlides() &&
      !this.#shouldSkipLongJumpReorder()
    ) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });
      });
    }

    const slide = this.#getSlideForSelection(requestedIndex, index);
    if (!slide) return;

    if (currentSlide === slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedControl(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide: slides[index],
        id: slides[index]?.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  /**
   * Re-attach to slides that were replaced from outside this component.
   *
   * The PDP sibling swap rebuilds the gallery's slides from Storefront data
   * without reconnecting this element, so connectedCallback never re-runs.
   * The IntersectionObserver is otherwise only wired at connect and on resize,
   * which would leave it watching the detached nodes: #visibleSlides drains,
   * #sync() short-circuits on the empty list, and scrolling stops updating the
   * current slide (dots and thumbnails freeze on the old index).
   */
  refreshAfterSlidesReplaced() {
    // `refs` is rebuilt by a MutationObserver, so the slides appended a moment
    // ago are not in `this.refs.slides` yet — wait for that to land before
    // reading them, or this re-attaches against the detached nodes.
    requestAnimationFrame(() => {
      if (!this.isConnected || !this.refs.slides?.length) return;

      // The caller resets the scroller to the start; keep the index in step so
      // the controls do not point at a slide that no longer exists.
      this.current = 0;
      // Re-observes the live slides (and rebuilds any loop clones).
      // #visibleSlides repopulates itself from the observer — deliberately not
      // calling #updateVisibleSlides here, which would read the not-yet-filled
      // list and mark every new slide aria-hidden.
      this.#refreshInfiniteLoopSlides();
      this.#updateControlsVisibility();
    });
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      const selectedControlIndex = this.#getControlIndexForSlideIndex(controls, value);
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === selectedControlIndex}`));
    }

    this.#centerSelectedControl(value, 'auto');

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * Cloned slides rendered before the first slide for seamless reverse looping on mobile.
   * @type {HTMLElement[]}
   */
  #loopStartClones = [];

  /**
   * Cloned slides rendered after the last slide for seamless forward looping on mobile.
   * @type {HTMLElement[]}
   */
  #loopEndClones = [];

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    this.#refreshInfiniteLoopSlides();

    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          } else if (this.#shouldUseMobileLoopSlides()) {
            // On mobile with infinite loop clones, the DOM order is
            // [clone-of-LAST, slide-0, ..., slide-N, clone-of-FIRST] and the
            // browser's default scrollLeft of 0 lands on the start clone —
            // showing the LAST image. Explicitly scroll past the start clone
            // to slide 0 so the first image is shown on initial render.
            this.select(0, undefined, { animate: false });
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        this.#refreshInfiniteLoopSlides();

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.#centerSelectedControl(index, 'auto');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.#normalizeInfiniteLoopPosition();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    if (this.hasAttribute('data-programmatic-media-sync')) return this.current;

    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = this.#getLogicalIndexForSlide(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  #usesFreeScroll = () => {
    const { scroller } = this.refs;

    if (!(scroller instanceof HTMLElement)) return false;

    const isAboutPage = document.body.classList.contains('page-handle-about');
    const isAboutPageFreeScrollCarousel = this.closest('[id$="carousel_AxxNrM"], [id$="carousel_daghme"]');

    if (!isAboutPage || !isAboutPageFreeScrollCarousel) return false;

    return getComputedStyle(scroller).scrollSnapType === 'none';
  };

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;
    if (event.target.closest('.slide__link')) {
      return;
    }
    if (
      event.target.closest(
        'button, a:not(.slide__link), input, textarea, select, [role="button"], .slide__video-controls, slideshow-controls, slideshow-arrows'
      )
    ) {
      return;
    }

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = !this.infinite && ((movingRight && this.atStart) || (movingLeft && this.atEnd));

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;
      const usesFreeScroll = this.#usesFreeScroll();

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      let newIndex = next + modifier;

      if (this.infinite) {
        if (newIndex < 0) newIndex = slides.length - 1;
        if (newIndex >= slides.length) newIndex = 0;
      } else {
        newIndex = clamp(newIndex, 0, slides.length - 1);
      }

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      if (usesFreeScroll) {
        const previousIndex = current;
        const syncedIndex = this.#sync();

        this.#centerSelectedControl(syncedIndex);

        if (syncedIndex !== previousIndex) {
          const syncedSlide = slides[syncedIndex];
          if (syncedSlide) {
            this.dispatchEvent(
              new SlideshowSelectEvent({
                index: syncedIndex,
                previousIndex,
                userInitiated: true,
                trigger: 'drag',
                slide: syncedSlide,
                id: syncedSlide.getAttribute('slide-id'),
              })
            );
          }
        }

        this.current = syncedIndex;
        this.resume();
        return;
      }

      this.#scroll.to(newSlide);

      this.#centerSelectedControl(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { scroller } = this.refs;
    const slides = this.#getRenderedSlides();
    if (!slides.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides = this.#visibleSlides.filter((slide) => slides.includes(slide));
        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedControl(index, behavior = 'smooth') {
    const selectedThumbnailIndex = this.#getControlIndexForSlideIndex(this.refs.thumbnails, index);
    const selectedThumbnail = this.refs.thumbnails?.[selectedThumbnailIndex];
    const { thumbnailsContainer } = this.refs;
    if (selectedThumbnail && thumbnailsContainer instanceof HTMLElement) {
      scrollIntoView(selectedThumbnail, {
        ancestor: thumbnailsContainer,
        behavior,
        block: 'center',
        inline: 'center',
      });
      return;
    }

    const selectedDotIndex = this.#getControlIndexForSlideIndex(this.refs.dots, index);
    const selectedDot = this.refs.dots?.[selectedDotIndex];
    if (!selectedDot) return;
    const dotsContainer = selectedDot.closest('.slideshow-controls__dots');
    if (!(dotsContainer instanceof HTMLElement)) return;
    const maxScrollLeft = Math.max(0, dotsContainer.scrollWidth - dotsContainer.clientWidth);
    const targetLeft =
      selectedDot.offsetLeft + selectedDot.offsetWidth / 2 - dotsContainer.clientWidth / 2;
    dotsContainer.scrollTo({
      left: clamp(targetLeft, 0, maxScrollLeft),
      behavior,
    });
  }

  #getMediaIdForSlideIndex(index) {
    const slide = this.slides?.[index];
    return slide instanceof HTMLElement ? slide.getAttribute('data-media-id') || '' : '';
  }

  #getControlMediaId(control) {
    if (!(control instanceof HTMLElement)) return '';

    return control.getAttribute('data-media-id') || control.closest('[data-media-id]')?.getAttribute('data-media-id') || '';
  }

  #getControlIndexForSlideIndex(controls, slideIndex) {
    if (!controls?.length) return slideIndex;

    const mediaId = this.#getMediaIdForSlideIndex(slideIndex);
    if (!mediaId) return slideIndex;

    const matchingIndex = controls.findIndex((control) => this.#getControlMediaId(control) === mediaId);
    return matchingIndex >= 0 ? matchingIndex : slideIndex;
  }

  #updateVisibleSlides() {
    const slides = this.#getRenderedSlides();
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }

  #getRenderedSlides() {
    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return [];

    return Array.from(scroller.querySelectorAll(':scope > slideshow-slide'));
  }

  #getLogicalIndexForSlide(slide) {
    const { slides } = this;
    if (!slides?.length) return 0;

    const cloneOf = slide.getAttribute('data-slide-clone-of');
    if (cloneOf != null) {
      const cloneIndex = parseInt(cloneOf, 10);
      if (!Number.isNaN(cloneIndex)) return cloneIndex;
    }

    const index = slides.indexOf(slide);
    return index >= 0 ? index : 0;
  }

  #getSlideForSelection(requestedIndex, normalizedIndex) {
    const { slides } = this;
    if (!slides?.length) return undefined;

    if (this.#shouldUseMobileLoopSlides()) {
      if (requestedIndex < 0) {
        return this.#loopStartClones[this.#loopStartClones.length - 1] ?? slides[normalizedIndex];
      }

      if (requestedIndex >= slides.length) {
        return this.#loopEndClones[0] ?? slides[normalizedIndex];
      }
    }

    return slides[normalizedIndex];
  }

  /**
   * Quick View uses a custom horizontal media layout where the long-jump reorder optimization
   * causes visible flashes. Let it use the normal scroll-to-slide behavior instead.
   * @returns {boolean}
   */
  #shouldSkipLongJumpReorder() {
    return this.closest('#quick-add-dialog') != null;
  }

  #shouldUseMobileLoopSlides() {
    const { slides } = this;
    return Boolean(
      this.infinite &&
        !this.hasAttribute('data-disable-mobile-loop') &&
        !mediaQueryLarge.matches &&
        slides &&
        slides.length > 1
    );
  }

  #createLoopClone(slide, index) {
    const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
    clone.removeAttribute('ref');
    clone.removeAttribute('reveal');
    clone.setAttribute('data-slide-clone-of', String(index));
    clone.setAttribute('data-slide-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');

    clone.querySelectorAll('[ref]').forEach((element) => element.removeAttribute('ref'));
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone.querySelectorAll('[data-slideshow-video]').forEach((element) => element.removeAttribute('data-slideshow-video'));
    clone.querySelectorAll('video').forEach((video) => {
      video.removeAttribute('autoplay');
      video.pause();
      video.preload = 'none';
    });

    return clone;
  }

  #refreshInfiniteLoopSlides() {
    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return;

    const currentSlide = this.slides?.[this.current] ?? this.initialSlide;

    this.#loopStartClones.forEach((slide) => slide.remove());
    this.#loopEndClones.forEach((slide) => slide.remove());
    this.#loopStartClones = [];
    this.#loopEndClones = [];

    if (!this.#shouldUseMobileLoopSlides()) {
      this.#visibleSlides = this.#visibleSlides.filter((slide) => slide.isConnected);
      this.#setupIntersectionObserver();
      if (this.#scroll && currentSlide instanceof HTMLElement) {
        this.#scroll.to(currentSlide, { instant: true });
      }
      return;
    }

    const { slides } = this;
    if (!slides?.length) return;

    const firstSlide = slides[0];
    const lastSlide = slides[slides.length - 1];
    if (!(firstSlide instanceof HTMLElement) || !(lastSlide instanceof HTMLElement)) return;

    const startClone = this.#createLoopClone(lastSlide, slides.length - 1);
    const endClone = this.#createLoopClone(firstSlide, 0);

    scroller.prepend(startClone);
    scroller.append(endClone);

    this.#loopStartClones = [startClone];
    this.#loopEndClones = [endClone];

    this.#visibleSlides = this.#visibleSlides.filter((slide) => slide.isConnected);
    this.#setupIntersectionObserver();

    if (currentSlide instanceof HTMLElement) {
      if (this.#scroll) {
        this.#scroll.to(currentSlide, { instant: true });
      } else {
        const paddingStart = parseFloat(getComputedStyle(scroller).paddingInlineStart) || 0;
        scroller.scrollLeft = currentSlide.offsetLeft - paddingStart;
      }
    }
  }

  #normalizeInfiniteLoopPosition() {
    if (!this.#shouldUseMobileLoopSlides() || !this.#scroll) return;

    const renderedSlides = this.#getRenderedSlides();
    const visibleSlides = this.visibleSlides.filter((slide) => renderedSlides.includes(slide));
    if (!visibleSlides.length) return;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const activeSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!activeSlide?.hasAttribute('data-slide-clone-of')) return;

    const logicalIndex = this.#getLogicalIndexForSlide(activeSlide);
    const targetSlide = this.slides?.[logicalIndex];
    if (!(targetSlide instanceof HTMLElement)) return;

    this.#scroll.to(targetSlide, { instant: true });
    this.current = logicalIndex;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
